/**
 * GenesysScene - engine root THREE.Scene. Aggregates ISceneEnvironmentSource
 * registrations and applies background/environment state each frame before render.
 */

import * as THREE from 'three';

import type { ISceneEnvironmentSource, SceneEnvironmentData } from '../interfaces.js';

export class GenesysScene extends THREE.Scene {
  /** Weak refs so destroyed components drop out without explicit unregister. */
  private _sceneEnvironmentSources: WeakRef<ISceneEnvironmentSource>[] = [];
  /** Shared zero rotation reused when a source omits rotation. */
  private _zeroEuler: THREE.Euler = new THREE.Euler(0, 0, 0);

  constructor() {
    super();
  }

  public addSceneEnvironment(source: ISceneEnvironmentSource): void {
    this._sceneEnvironmentSources.push(new WeakRef(source));
  }

  public removeSceneEnvironment(source: ISceneEnvironmentSource): void {
    this._sceneEnvironmentSources = this._sceneEnvironmentSources.filter(ref => ref.deref() !== source);
  }

  public override onBeforeRender(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, geometry: THREE.BufferGeometry, material: THREE.Material, group: THREE.Group): void {
    this.applySceneEnvironment();
    super.onBeforeRender(renderer, scene, camera, geometry, material, group);
  }

  public override tickInEditor(deltaTime: number): void {
    super.tickInEditor(deltaTime);
    this.applySceneEnvironment();
  }

  /**
   * Copies the first visible registered source onto scene background/environment fields.
   * Clears to defaults when no eligible source remains.
   */
  private applySceneEnvironment(): void {
    let sceneEnvironmentData: SceneEnvironmentData | null = null;
    for (const ref of this._sceneEnvironmentSources) {
      const source = ref.deref();
      if (!source) continue;
      const data = source.getSceneEnvironmentData();
      if (data.sceneComponent.isHidden() || data.sceneComponent.isHiddenInGame()) continue;
      sceneEnvironmentData = data;
      break;
    }

    if (sceneEnvironmentData) {
      this.background = sceneEnvironmentData.background ?? null;
      this.backgroundIntensity = sceneEnvironmentData.backgroundIntensity ?? 1.0;
      this.backgroundBlurriness = sceneEnvironmentData.backgroundBlurriness ?? 0.0;
      this.backgroundRotation = sceneEnvironmentData.backgroundRotation ?? this._zeroEuler;
      this.environment = sceneEnvironmentData.environment ?? null;
      this.environmentIntensity = sceneEnvironmentData.environmentIntensity ?? 1;
      this.environmentRotation = sceneEnvironmentData.environmentRotation ?? this._zeroEuler;
    } else {
      this.background = null;
      this.backgroundIntensity = 1;
      this.backgroundBlurriness = 0;
      this.backgroundRotation = this._zeroEuler;
      this.environment = null;
      this.environmentIntensity = 1;
      this.environmentRotation = this._zeroEuler;
    }
  }
}
